'use client'

import { revalidateTags } from '@repo/sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'

interface IQueryContext {
  addKey: (key: string) => void
  keys: string[]
  refetch: () => void
  removeKey: (key: string) => void
}

export const QueryContext = createContext<IQueryContext>({
  addKey: () => null,
  keys: [],
  refetch: () => null,
  removeKey: () => null,
})

export const QueryContextProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient()
  const [keys, setKeys] = useState<string[]>([])

  const addKey = (key: string) => {
    setKeys([...keys, key])
  }

  const removeKey = (key: string) => {
    setKeys(keys.filter(k => k !== key))
  }

  const refetch = async () => {
    await revalidateTags(keys)
    await queryClient.invalidateQueries({
      queryKey: keys,
    })
  }

  const value = useMemo(
    () => ({
      addKey,
      keys,
      refetch,
      removeKey,
    }),
    [keys],
  )

  return <QueryContext.Provider value={value}>{children}</QueryContext.Provider>
}

export const useQueryContext = () => {
  const context = useContext(QueryContext)

  if (!context) {
    throw new Error('useQueryContext must be used within a QueryContextProvider')
  }

  return context
}
